/**
 * @file Shared theme read/write mechanism — `<html data-theme>` +
 *       cross-host cookie.
 * @module @labcraft/ui/chrome/theme
 *
 * Extracted from {@link ThemeToggle} so any surface that needs to flip
 * the theme (the lab Settings modal's "Appearance" control, future
 * pre-paint bootstrap scripts) drives the *same* state — the document
 * `[data-theme]` attribute plus the `.labcraft.dev`-scoped cookie — and
 * never reinvents theme persistence. `ThemeToggle` now composes these
 * helpers; the read/write/persistence semantics are unchanged.
 *
 * Persistence is a cookie at `.labcraft.dev` so the choice applies
 * across www / lab / admin without re-toggling per host. On dev hosts
 * (localhost / 127.0.0.1 / Vite ports) the Domain attribute is omitted
 * so the cookie still works origin-scoped.
 */

/** The two themes the document cascade switches on. */
export type Theme = 'light' | 'dark'

/** Cookie name carrying the cross-host theme override. */
const COOKIE_NAME = 'lc-theme'

/** Cookie lifetime — one year. */
const COOKIE_MAX_AGE_S = 60 * 60 * 24 * 365

/**
 * Read the live theme from `<html data-theme>` (set by the bootstrap
 * script or a prior {@link setTheme} call).
 *
 * @returns `'dark'` when the attribute is `dark`, else `'light'`.
 */
export function readCurrentTheme(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'dark'
    ? 'dark'
    : 'light'
}

/**
 * Read the theme-override cookie.
 *
 * @returns The stored {@link Theme}, or `null` when absent.
 */
export function readThemeCookie(): Theme | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=(light|dark)\\b`),
  )
  return match ? (match[1] as Theme) : null
}

/**
 * Write the theme-override cookie. Scoped to `.labcraft.dev` on
 * production subdomains so all three hosts share the preference; omits
 * Domain on dev hosts so the cookie still works origin-scoped.
 *
 * @param theme - The theme to persist.
 */
export function writeThemeCookie(theme: Theme): void {
  const parts = [
    `${COOKIE_NAME}=${theme}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_S}`,
    'SameSite=Lax',
  ]
  if (window.location.hostname.endsWith('.labcraft.dev')) {
    parts.push('Domain=.labcraft.dev')
  }
  document.cookie = parts.join('; ')
}

/**
 * Set the theme: flip `<html data-theme>` and persist the cookie. The
 * single mutation point every theme control routes through.
 *
 * @param theme - The theme to apply.
 */
export function setTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
  writeThemeCookie(theme)
}
